/**
 * StudioPlusBuilder - AI-assisted simulation blueprint builder
 * 
 * Clean two-column layout with:
 * - Left: Form fields for blueprint editing
 * - Right: Chat interface with AI assistant
 * 
 * Uses Tailwind CSS for styling consistency
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, Sparkles, Rocket, Save, X, Plus, Trash2, Loader2 } from 'lucide-react';

const DEBOUNCE_DELAY = 800;
import axios from 'axios';
import { API_BASE, getAuthHeaders } from '../../utils/api';
import { StudioPlusChat } from './StudioPlusChat';
import { AppHeader } from '../../v5/components/AppHeader';

interface Agent {
  agent_name: string;
  role: string;
  capabilities: string[];
  traits: string[];
  goals: string[];
  interaction_rules: string;
}

interface DynamicEvent {
  event_id: string;
  description: string;
  trigger_condition: string;
}

interface SimulationBlueprint {
  simulation_id: string;
  metadata: {
    name: string;
    theme: string;
    primary_objective: string;
    expected_duration_steps: number;
    difficulty_level: string;
    intended_agents: string[];
    evaluation_focus: string[];
    simulation_run_duration_min?: number;
  };
  environment: {
    type: string;
    state_variables: Record<string, string>;
    rules: string;
    locations: string[];
    dynamic_events: DynamicEvent[];
  };
  story_skeleton: {
    starting_context: string;
    key_stakeholders: string[];
    primary_conflicts: string[];
    branch_points: any[];
    possible_outcomes: string[];
  };
  agents: Agent[];
  simulation_flow: {
    turn_definition: string;
    trigger_mechanisms: string[];
    action_resolution: string;
    conflict_resolution: string;
    branching_logic: string;
  };
  evaluation_guidance: {
    evaluation_agent_goals: string[];
    metrics: Record<string, string>;
    weighting: Record<string, number>;
    observation_window: string;
    report_structure: string[];
  };
  optional_metadata: {
    randomization_seed?: number;
    logging: boolean;
    narrative_tone: string;
    extensions: string[];
    enable_real_world_problems: boolean;
  };
}

// Reusable input class
const inputClass = "w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-colors";
const textareaClass = "w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-colors resize-none";
const selectClass = "w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-colors";
const labelClass = "block text-sm font-medium text-slate-700 mb-1";

// Section component moved outside to prevent re-creation on every render
function Section({ 
  id, 
  title, 
  badge, 
  children,
  isExpanded,
  onToggle
}: { 
  id: string; 
  title: string; 
  badge?: React.ReactNode;
  children: React.ReactNode;
  isExpanded: boolean;
  onToggle: (id: string) => void;
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <button
        onClick={() => onToggle(id)}
        className="w-full flex justify-between items-center p-4 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="font-semibold text-slate-900">{title}</span>
          {badge}
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </button>
      {isExpanded && (
        <div className="p-4 pt-0 space-y-4 border-t border-slate-100">
          {children}
        </div>
      )}
    </div>
  );
}

export function StudioPlusBuilder() {
  const navigate = useNavigate();
  
  const [isComplete, setIsComplete] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const [studioPlusSession, setStudioPlusSession] = useState<any>(null);
  const [pendingManualUpdates, setPendingManualUpdates] = useState<Record<string, any>>({});
  const [toastMessage, setToastMessage] = useState<{ type: 'success' | 'error' | 'warning'; message: string } | null>(null);
  
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    metadata: true,
    user: true,
    environment: false,
    story: false,
    agents: false,
    evaluation: false,
    optional: false,
  });
  
  const [sliderValue, setSliderValue] = useState<number>(2);
  const sliderTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  
  // Debouncing for manual updates - use refs to avoid re-renders
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const pendingUpdatesRef = useRef<Record<string, any>>({});
  const dirtyFieldsRef = useRef<Set<string>>(new Set());
  const inFlightFieldsRef = useRef<Set<string>>(new Set()); // Fields currently being sent to API
  const userEditedFieldsRef = useRef<Set<string>>(new Set()); // Fields user has edited (persists until explicitly cleared)

  const [blueprint, setBlueprint] = useState<SimulationBlueprint>({
    simulation_id: `sim_${Date.now()}`,
    metadata: {
      name: '',
      theme: '',
      primary_objective: '',
      expected_duration_steps: 10,
      difficulty_level: 'Medium',
      intended_agents: [],
      evaluation_focus: [],
      simulation_run_duration_min: undefined,
    },
    environment: {
      type: 'Office',
      state_variables: {},
      rules: '',
      locations: [],
      dynamic_events: [],
    },
    story_skeleton: {
      starting_context: '',
      key_stakeholders: [],
      primary_conflicts: [],
      branch_points: [],
      possible_outcomes: [],
    },
    agents: [],
    simulation_flow: {
      turn_definition: 'Each turn represents a 30-minute interval',
      trigger_mechanisms: ['time_step', 'agent_action'],
      action_resolution: '',
      conflict_resolution: '',
      branching_logic: '',
    },
    evaluation_guidance: {
      evaluation_agent_goals: [],
      metrics: {},
      weighting: {},
      observation_window: 'Full simulation',
      report_structure: ['Summary', 'Detailed Analysis', 'Recommendations'],
    },
    optional_metadata: {
      logging: true,
      narrative_tone: 'Professional',
      extensions: [],
      enable_real_world_problems: false,
    },
  });

  const [userAgent, setUserAgent] = useState<Agent>({
    agent_name: 'User',
    role: '',
    capabilities: [],
    traits: [],
    goals: [],
    interaction_rules: '',
  });

  const showToast = (type: 'success' | 'error' | 'warning', message: string) => {
    setToastMessage({ type, message });
    setTimeout(() => setToastMessage(null), 3000);
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    setSliderValue(value);
    if (sliderTimeoutRef.current) {
      clearTimeout(sliderTimeoutRef.current);
    }
    sliderTimeoutRef.current = setTimeout(() => {
      setBlueprint(prev => ({
        ...prev,
        metadata: { ...prev.metadata, simulation_run_duration_min: value }
      }));
      trackManualUpdate('metadata.simulation_run_duration_min', value);
    }, 300);
  };

  const addDynamicEvent = () => {
    setBlueprint(prev => {
      const existingCount = prev.environment.dynamic_events.length;
      const totalMinutes = prev.metadata.simulation_run_duration_min || 10;
      // Calculate trigger time: spread events at equal intervals based on time limit
      const triggerTime = Math.round((totalMinutes * (existingCount + 1)) / (existingCount + 2));
      
      return {
        ...prev,
        environment: {
          ...prev.environment,
          dynamic_events: [...prev.environment.dynamic_events, {
            event_id: `ev_${existingCount + 1}`,
            description: '',
            trigger_condition: `time_step == ${triggerTime}`,
          }],
        },
      };
    });
  };

  const redistributeDynamicEvents = () => {
    setBlueprint(prev => {
      const totalMinutes = prev.metadata.simulation_run_duration_min || 10;
      const eventCount = prev.environment.dynamic_events.length;
      if (eventCount === 0) return prev;
      
      const updatedEvents = prev.environment.dynamic_events.map((event, index) => {
        // Spread events evenly based on time limit
        const triggerTime = Math.round((totalMinutes * (index + 1)) / (eventCount + 1));
        return {
          ...event,
          trigger_condition: `time_step == ${triggerTime}`,
        };
      });
      
      return {
        ...prev,
        environment: {
          ...prev.environment,
          dynamic_events: updatedEvents,
        },
      };
    });
  };

  const removeDynamicEvent = (index: number) => {
    setBlueprint(prev => ({
      ...prev,
      environment: {
        ...prev.environment,
        dynamic_events: prev.environment.dynamic_events.filter((_, i) => i !== index),
      },
    }));
  };

  const flushManualUpdates = useCallback(() => {
    const updates = pendingUpdatesRef.current;
    if (Object.keys(updates).length > 0) {
      // Move dirty fields to in-flight (they'll be cleared after API response)
      dirtyFieldsRef.current.forEach(field => inFlightFieldsRef.current.add(field));
      
      setPendingManualUpdates(updates);
      pendingUpdatesRef.current = {};
      dirtyFieldsRef.current = new Set();
    }
  }, []);

  const trackManualUpdate = useCallback((path: string, value: any) => {
    // Mark field as dirty to prevent overwrites during editing (no state update = no re-render)
    dirtyFieldsRef.current.add(path);
    // Also track in persistent user-edited fields
    userEditedFieldsRef.current.add(path);
    
    // Accumulate updates
    pendingUpdatesRef.current = {
      ...pendingUpdatesRef.current,
      [path]: value
    };
    
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Set new debounced flush
    debounceTimerRef.current = setTimeout(() => {
      flushManualUpdates();
    }, DEBOUNCE_DELAY);
  }, [flushManualUpdates]);

  // Handle blur to immediately flush updates
  const handleFieldBlur = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    flushManualUpdates();
  }, [flushManualUpdates]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleBlueprintUpdate = useCallback((newBlueprint: any) => {
    // Combine ALL protected fields - dirty, in-flight, and user-edited
    const protectedFields = new Set([
      ...dirtyFieldsRef.current,
      ...inFlightFieldsRef.current,
      ...userEditedFieldsRef.current
    ]);
    
    setBlueprint(prev => {
      // Handle dynamic_events explicitly - use new array if present, otherwise keep old
      const newDynamicEvents = newBlueprint.environment?.dynamic_events !== undefined 
        ? newBlueprint.environment.dynamic_events 
        : prev.environment.dynamic_events;
      
      // Filter out updates for protected fields (fields user edited)
      const filteredMetadata = { ...prev.metadata };
      if (newBlueprint.metadata) {
        Object.entries(newBlueprint.metadata).forEach(([key, value]) => {
          const path = `metadata.${key}`;
          if (!protectedFields.has(path)) {
            (filteredMetadata as any)[key] = value;
          }
        });
      }
      
      const filteredStorySkeleton = { ...prev.story_skeleton };
      if (newBlueprint.story_skeleton) {
        Object.entries(newBlueprint.story_skeleton).forEach(([key, value]) => {
          const path = `story_skeleton.${key}`;
          if (!protectedFields.has(path)) {
            (filteredStorySkeleton as any)[key] = value;
          }
        });
      }
      
      return {
        ...prev,
        ...newBlueprint,
        metadata: filteredMetadata,
        environment: { 
          ...prev.environment, 
          ...newBlueprint.environment,
          dynamic_events: newDynamicEvents 
        },
        story_skeleton: filteredStorySkeleton,
        agents: newBlueprint.agents !== undefined ? newBlueprint.agents : prev.agents,
      };
    });
    
    // Sync sliderValue when time limit is updated by AI (check for existence, not truthiness)
    if (newBlueprint.metadata && typeof newBlueprint.metadata.simulation_run_duration_min === 'number') {
      if (!protectedFields.has('metadata.simulation_run_duration_min')) {
        setSliderValue(newBlueprint.metadata.simulation_run_duration_min);
      }
    }
    
    // Sync userAgent state when updated by AI (only for non-protected fields)
    if (newBlueprint.user_agent) {
      setUserAgent(prev => {
        const updated = { ...prev };
        Object.entries(newBlueprint.user_agent).forEach(([key, value]) => {
          const path = `user_agent.${key}`;
          if (!protectedFields.has(path)) {
            (updated as any)[key] = value;
          }
        });
        return updated;
      });
    }
  }, []);

  const addAgent = () => {
    const newAgent: Agent = {
      agent_name: `Agent ${blueprint.agents.length + 1}`,
      role: '',
      capabilities: [],
      traits: [],
      goals: [],
      interaction_rules: '',
    };
    setBlueprint(prev => ({
      ...prev,
      agents: [...prev.agents, newAgent]
    }));
  };

  const removeAgent = (index: number) => {
    setBlueprint(prev => ({
      ...prev,
      agents: prev.agents.filter((_, i) => i !== index)
    }));
  };

  const updateAgent = (index: number, field: keyof Agent, value: any) => {
    setBlueprint(prev => ({
      ...prev,
      agents: prev.agents.map((agent, i) => 
        i === index ? { ...agent, [field]: value } : agent
      )
    }));
    trackManualUpdate(`agents.${index}.${field}`, value);
  };

  const handleCancel = () => {
    navigate('/app');
  };

  const handleSave = async () => {
    if (!blueprint.metadata.name) {
      showToast('warning', 'Please provide a simulation name');
      return;
    }

    setIsSaving(true);
    try {
      const authHeaders = await getAuthHeaders();
      
      const finalBlueprint = {
        ...blueprint,
        agents: [
          { ...userAgent, agent_name: `${userAgent.agent_name} (User)` },
          ...blueprint.agents,
          {
            agent_name: 'Evaluation Agent',
            role: 'Observer and evaluator',
            capabilities: ['Observe', 'Assess', 'Report'],
            traits: ['Objective', 'Analytical'],
            goals: blueprint.evaluation_guidance.evaluation_agent_goals,
            interaction_rules: '',
          }
        ],
      };

      await axios.post(`${API_BASE}/custom-simulations`, {
        title: blueprint.metadata.name,
        description: blueprint.metadata.primary_objective,
        role: userAgent.role,
        difficulty: blueprint.metadata.difficulty_level,
        duration: blueprint.metadata.simulation_run_duration_min 
          ? `${blueprint.metadata.simulation_run_duration_min} minutes` 
          : '30 minutes',
        blueprint: finalBlueprint,
        is_global: false,
        is_programming: false,
        tags: [],
      }, { headers: authHeaders });

      showToast('success', 'Simulation saved successfully');
      navigate('/app');
    } catch (error: any) {
      showToast('error', error.message || 'Failed to save simulation');
    } finally {
      setIsSaving(false);
    }
  };

  const handleLaunch = async () => {
    if (!blueprint.metadata.name || !blueprint.metadata.primary_objective || !userAgent.role) {
      showToast('warning', 'Please fill in simulation name, objective, and user role');
      return;
    }

    setIsLaunching(true);
    try {
      const authHeaders = await getAuthHeaders();
      
      const finalBlueprint = {
        ...blueprint,
        agents: [
          { ...userAgent, agent_name: `${userAgent.agent_name} (User)` },
          ...blueprint.agents,
          {
            agent_name: 'Evaluation Agent',
            role: 'Observer and evaluator',
            capabilities: ['Observe', 'Assess', 'Report'],
            traits: ['Objective', 'Analytical'],
            goals: blueprint.evaluation_guidance.evaluation_agent_goals,
            interaction_rules: '',
          }
        ],
      };

      const response = await axios.post(`${API_BASE}/initialize`, {
        blueprint: finalBlueprint,
      }, { headers: authHeaders });

      if (response.data.session_id) {
        navigate(`/app/simulation/${response.data.session_id}`);
      }
    } catch (error: any) {
      showToast('error', error.message || 'Failed to launch simulation');
    } finally {
      setIsLaunching(false);
    }
  };

  return (
    <div className="h-screen bg-slate-50 overflow-hidden flex flex-col">
      {/* Toast Notification */}
      {toastMessage && (
        <div className={`fixed top-20 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${
          toastMessage.type === 'success' ? 'bg-emerald-500 text-white' :
          toastMessage.type === 'error' ? 'bg-red-500 text-white' :
          'bg-amber-500 text-white'
        }`}>
          {toastMessage.message}
        </div>
      )}

      {/* App Header */}
      <AppHeader activePage="studio-plus" />
      
      {/* Action Toolbar */}
      <div className="sticky top-16 z-40 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-emerald-600 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-slate-900">Studio+</h1>
                <p className="text-xs text-slate-500">AI-Assisted Builder</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleCancel}
                className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
                <span>Cancel</span>
              </button>
              
              <button
                onClick={handleSave}
                disabled={isSaving || !isComplete}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  isComplete && !isSaving
                    ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                    : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                }`}
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                <span>Save</span>
              </button>
              
              <button
                onClick={handleLaunch}
                disabled={isLaunching || !isComplete}
                className={`flex items-center gap-2 px-5 py-2 rounded-lg transition-all ${
                  isComplete && !isLaunching
                    ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-500/25'
                    : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                }`}
              >
                {isLaunching ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Rocket className="w-4 h-4" />
                )}
                <span>Launch</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Two Column Layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Column - Form Fields */}
        <div className="w-1/2 overflow-y-auto p-6 border-r border-slate-200">
          <div className="max-w-2xl mx-auto space-y-4">
            
            {/* Metadata Section */}
            <Section id="metadata" title="Metadata" isExpanded={expandedSections.metadata} onToggle={toggleSection}>
              <div>
                <label className={labelClass}>Simulation Name</label>
                <input
                  type="text"
                  value={blueprint.metadata.name}
                  onChange={(e) => {
                    setBlueprint(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, name: e.target.value }
                    }));
                    trackManualUpdate('metadata.name', e.target.value);
                  }}
                  onBlur={handleFieldBlur}
                  placeholder="Enter simulation name"
                  className={inputClass}
                />
              </div>
              
              <div>
                <label className={labelClass}>Theme</label>
                <input
                  type="text"
                  value={blueprint.metadata.theme}
                  onChange={(e) => {
                    setBlueprint(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, theme: e.target.value }
                    }));
                    trackManualUpdate('metadata.theme', e.target.value);
                  }}
                  onBlur={handleFieldBlur}
                  placeholder="e.g., Leadership, Customer Service"
                  className={inputClass}
                />
              </div>
              
              <div>
                <label className={labelClass}>Primary Objective</label>
                <textarea
                  value={blueprint.metadata.primary_objective}
                  onChange={(e) => {
                    setBlueprint(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, primary_objective: e.target.value }
                    }));
                    trackManualUpdate('metadata.primary_objective', e.target.value);
                  }}
                  onBlur={handleFieldBlur}
                  placeholder="What should this simulation evaluate?"
                  rows={2}
                  className={textareaClass}
                />
              </div>

              <div>
                <label className={labelClass}>Difficulty</label>
                <select
                  value={blueprint.metadata.difficulty_level}
                  onChange={(e) => {
                    setBlueprint(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, difficulty_level: e.target.value }
                    }));
                    trackManualUpdate('metadata.difficulty_level', e.target.value);
                  }}
                  className={selectClass}
                >
                  <option value="Easy">Easy</option>
                  <option value="Medium">Medium</option>
                  <option value="Hard">Hard</option>
                </select>
              </div>

              {/* Time Limit Slider */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className={labelClass}>Time Limit (minutes)</label>
                  <span className="text-sm font-medium text-emerald-600">
                    {sliderValue || blueprint.metadata.simulation_run_duration_min || 2} min
                  </span>
                </div>
                <input
                  type="range"
                  value={sliderValue || blueprint.metadata.simulation_run_duration_min || 2}
                  min={1}
                  max={60}
                  step={1}
                  onChange={handleSliderChange}
                  className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-emerald-500"
                />
              </div>
            </Section>

            {/* User Role Section */}
            <Section id="user" title="User Role" isExpanded={expandedSections.user} onToggle={toggleSection}>
              <div>
                <label className={labelClass}>Role Title</label>
                <input
                  type="text"
                  value={userAgent.role}
                  onChange={(e) => {
                    setUserAgent(prev => ({ ...prev, role: e.target.value }));
                    trackManualUpdate('user_agent.role', e.target.value);
                  }}
                  onBlur={handleFieldBlur}
                  placeholder="e.g., Project Manager, Team Lead"
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>Capabilities</label>
                <textarea
                  value={userAgent.capabilities.join('\n')}
                  onChange={(e) => {
                    const value = e.target.value.split('\n').filter(c => c.trim());
                    setUserAgent(prev => ({ ...prev, capabilities: value }));
                    trackManualUpdate('user_agent.capabilities', value);
                  }}
                  onBlur={handleFieldBlur}
                  placeholder="Enter each capability on a new line"
                  rows={2}
                  className={textareaClass}
                />
              </div>

              <div>
                <label className={labelClass}>Traits</label>
                <textarea
                  value={userAgent.traits.join('\n')}
                  onChange={(e) => {
                    const value = e.target.value.split('\n').filter(t => t.trim());
                    setUserAgent(prev => ({ ...prev, traits: value }));
                    trackManualUpdate('user_agent.traits', value);
                  }}
                  onBlur={handleFieldBlur}
                  placeholder="Enter each trait on a new line"
                  rows={2}
                  className={textareaClass}
                />
              </div>
              
              <div>
                <label className={labelClass}>Your Goals</label>
                <textarea
                  value={userAgent.goals.join('\n')}
                  onChange={(e) => {
                    const value = e.target.value.split('\n').filter(g => g.trim());
                    setUserAgent(prev => ({ ...prev, goals: value }));
                    trackManualUpdate('user_agent.goals', value);
                  }}
                  onBlur={handleFieldBlur}
                  placeholder="Enter each goal on a new line"
                  rows={2}
                  className={textareaClass}
                />
              </div>
            </Section>

            {/* Story Section */}
            <Section id="story" title="Scenario" isExpanded={expandedSections.story} onToggle={toggleSection}>
              <div>
                <label className={labelClass}>Starting Context</label>
                <textarea
                  value={blueprint.story_skeleton.starting_context}
                  onChange={(e) => {
                    setBlueprint(prev => ({
                      ...prev,
                      story_skeleton: { ...prev.story_skeleton, starting_context: e.target.value }
                    }));
                    trackManualUpdate('story_skeleton.starting_context', e.target.value);
                  }}
                  onBlur={handleFieldBlur}
                  placeholder="Describe the initial scenario..."
                  rows={3}
                  className={textareaClass}
                />
              </div>

              <div>
                <label className={labelClass}>Key Stakeholders</label>
                <textarea
                  value={blueprint.story_skeleton.key_stakeholders.join('\n')}
                  onChange={(e) => {
                    const value = e.target.value.split('\n').filter(s => s.trim());
                    setBlueprint(prev => ({
                      ...prev,
                      story_skeleton: { ...prev.story_skeleton, key_stakeholders: value }
                    }));
                    trackManualUpdate('story_skeleton.key_stakeholders', value);
                  }}
                  onBlur={handleFieldBlur}
                  placeholder="Enter each stakeholder on a new line"
                  rows={2}
                  className={textareaClass}
                />
              </div>
              
              <div>
                <label className={labelClass}>Key Challenges</label>
                <textarea
                  value={blueprint.story_skeleton.primary_conflicts.join('\n')}
                  onChange={(e) => {
                    const value = e.target.value.split('\n').filter(c => c.trim());
                    setBlueprint(prev => ({
                      ...prev,
                      story_skeleton: { ...prev.story_skeleton, primary_conflicts: value }
                    }));
                    trackManualUpdate('story_skeleton.primary_conflicts', value);
                  }}
                  onBlur={handleFieldBlur}
                  placeholder="Enter each challenge on a new line"
                  rows={2}
                  className={textareaClass}
                />
              </div>

              <div>
                <label className={labelClass}>Possible Outcomes</label>
                <textarea
                  value={blueprint.story_skeleton.possible_outcomes.join('\n')}
                  onChange={(e) => {
                    const value = e.target.value.split('\n').filter(o => o.trim());
                    setBlueprint(prev => ({
                      ...prev,
                      story_skeleton: { ...prev.story_skeleton, possible_outcomes: value }
                    }));
                    trackManualUpdate('story_skeleton.possible_outcomes', value);
                  }}
                  onBlur={handleFieldBlur}
                  placeholder="Enter each possible outcome on a new line"
                  rows={2}
                  className={textareaClass}
                />
              </div>
            </Section>

            {/* Agents Section */}
            <Section 
              id="agents" 
              title="Other Characters"
              isExpanded={expandedSections.agents}
              onToggle={toggleSection}
              badge={
                <span className="px-2 py-0.5 text-xs bg-emerald-100 text-emerald-700 rounded-full font-medium">
                  {blueprint.agents.length}
                </span>
              }
            >
              {blueprint.agents.map((agent, index) => (
                <div key={index} className="bg-slate-50 rounded-lg p-4 space-y-3 border border-slate-200">
                  <div className="flex items-center justify-between gap-2">
                    <input
                      type="text"
                      value={agent.agent_name}
                      onChange={(e) => updateAgent(index, 'agent_name', e.target.value)}
                      placeholder="Character name"
                      className={`${inputClass} font-semibold`}
                    />
                    <button
                      onClick={() => removeAgent(index)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <input
                    type="text"
                    value={agent.role}
                    onChange={(e) => updateAgent(index, 'role', e.target.value)}
                    placeholder="Role description"
                    className={inputClass}
                  />
                  <textarea
                    value={agent.traits.join(', ')}
                    onChange={(e) => updateAgent(index, 'traits', e.target.value.split(',').map(t => t.trim()).filter(t => t))}
                    placeholder="Traits (comma separated)"
                    rows={1}
                    className={textareaClass}
                  />
                  <textarea
                    value={agent.goals.join('\n')}
                    onChange={(e) => updateAgent(index, 'goals', e.target.value.split('\n').filter(g => g.trim()))}
                    placeholder="Goals (one per line)"
                    rows={2}
                    className={textareaClass}
                  />
                </div>
              ))}
              
              <button
                onClick={addAgent}
                className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-slate-300 rounded-lg text-slate-500 hover:text-emerald-600 hover:border-emerald-400 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>Add Character</span>
              </button>
            </Section>

            {/* Environment Section */}
            <Section 
              id="environment" 
              title="Environment"
              isExpanded={expandedSections.environment}
              onToggle={toggleSection}
              badge={blueprint.environment.dynamic_events.length > 0 ? (
                <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full font-medium">
                  {blueprint.environment.dynamic_events.length} events
                </span>
              ) : undefined}
            >
              <div>
                <label className={labelClass}>Environment Type</label>
                <select
                  value={blueprint.environment.type}
                  onChange={(e) => {
                    setBlueprint(prev => ({
                      ...prev,
                      environment: { ...prev.environment, type: e.target.value }
                    }));
                    trackManualUpdate('environment.type', e.target.value);
                  }}
                  className={selectClass}
                >
                  <option value="Office">Office</option>
                  <option value="Remote">Remote</option>
                  <option value="Hybrid">Hybrid</option>
                  <option value="Customer-facing">Customer-facing</option>
                  <option value="Healthcare">Healthcare</option>
                  <option value="Education">Education</option>
                </select>
              </div>

              <div>
                <label className={labelClass}>Locations</label>
                <textarea
                  value={blueprint.environment.locations.join('\n')}
                  onChange={(e) => {
                    const value = e.target.value.split('\n').filter(l => l.trim());
                    setBlueprint(prev => ({
                      ...prev,
                      environment: { ...prev.environment, locations: value }
                    }));
                    trackManualUpdate('environment.locations', value);
                  }}
                  onBlur={handleFieldBlur}
                  placeholder="Enter each location on a new line"
                  rows={2}
                  className={textareaClass}
                />
              </div>

              <div>
                <label className={labelClass}>Rules</label>
                <textarea
                  value={blueprint.environment.rules}
                  onChange={(e) => {
                    setBlueprint(prev => ({
                      ...prev,
                      environment: { ...prev.environment, rules: e.target.value }
                    }));
                    trackManualUpdate('environment.rules', e.target.value);
                  }}
                  onBlur={handleFieldBlur}
                  placeholder="Define environment rules and constraints..."
                  rows={2}
                  className={textareaClass}
                />
              </div>

              {/* Dynamic Events */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className={labelClass}>Dynamic Events</label>
                  <div className="flex items-center gap-2">
                    {blueprint.environment.dynamic_events.length > 0 && (
                      <button
                        onClick={redistributeDynamicEvents}
                        className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
                        title="Spread events evenly across simulation duration"
                      >
                        <Sparkles className="w-3 h-3" />
                        Redistribute
                      </button>
                    )}
                    <button
                      onClick={addDynamicEvent}
                      className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" />
                      Add Event
                    </button>
                  </div>
                </div>
                <div className="space-y-3">
                  {blueprint.environment.dynamic_events.map((event, index) => (
                    <div key={index} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                      <div className="flex items-start justify-between mb-3">
                        <span className="text-xs font-semibold text-slate-600 bg-slate-200 px-2 py-1 rounded">Event {index + 1}</span>
                        <button
                          onClick={() => removeDynamicEvent(index)}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Description</label>
                          <textarea
                            value={event.description}
                            onChange={(e) => {
                              const newEvents = [...blueprint.environment.dynamic_events];
                              newEvents[index] = { ...newEvents[index], description: e.target.value };
                              setBlueprint(prev => ({
                                ...prev,
                                environment: { ...prev.environment, dynamic_events: newEvents }
                              }));
                              trackManualUpdate(`environment.dynamic_events.${index}.description`, e.target.value);
                            }}
                            onBlur={handleFieldBlur}
                            placeholder="Describe what happens during this event..."
                            rows={2}
                            className={textareaClass}
                          />
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Trigger Condition</label>
                          <input
                            type="text"
                            value={event.trigger_condition}
                            onChange={(e) => {
                              const newEvents = [...blueprint.environment.dynamic_events];
                              newEvents[index] = { ...newEvents[index], trigger_condition: e.target.value };
                              setBlueprint(prev => ({
                                ...prev,
                                environment: { ...prev.environment, dynamic_events: newEvents }
                              }));
                              trackManualUpdate(`environment.dynamic_events.${index}.trigger_condition`, e.target.value);
                            }}
                            onBlur={handleFieldBlur}
                            placeholder="e.g., time_step == 5"
                            className={inputClass}
                          />
                          <p className="text-xs text-slate-400 mt-1">e.g., time_step == 5, time_step &gt; 3, time_step &lt; 8</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  {blueprint.environment.dynamic_events.length === 0 && (
                    <p className="text-sm text-slate-400 text-center py-2">
                      No dynamic events defined. The AI can generate these for you.
                    </p>
                  )}
                </div>
              </div>
            </Section>

            {/* Evaluation Section */}
            <Section id="evaluation" title="Evaluation" isExpanded={expandedSections.evaluation} onToggle={toggleSection}>
              <div>
                <label className={labelClass}>Evaluation Goals</label>
                <textarea
                  value={blueprint.evaluation_guidance.evaluation_agent_goals.join('\n')}
                  onChange={(e) => {
                    const value = e.target.value.split('\n').filter(g => g.trim());
                    setBlueprint(prev => ({
                      ...prev,
                      evaluation_guidance: { ...prev.evaluation_guidance, evaluation_agent_goals: value }
                    }));
                    trackManualUpdate('evaluation_guidance.evaluation_agent_goals', value);
                  }}
                  onBlur={handleFieldBlur}
                  placeholder="What should the evaluator assess? (one per line)"
                  rows={3}
                  className={textareaClass}
                />
              </div>

              <div>
                <label className={labelClass}>Evaluation Focus Areas</label>
                <textarea
                  value={blueprint.metadata.evaluation_focus.join('\n')}
                  onChange={(e) => {
                    const value = e.target.value.split('\n').filter(f => f.trim());
                    setBlueprint(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, evaluation_focus: value }
                    }));
                    trackManualUpdate('metadata.evaluation_focus', value);
                  }}
                  onBlur={handleFieldBlur}
                  placeholder="Enter focus areas (one per line)"
                  rows={2}
                  className={textareaClass}
                />
              </div>
            </Section>

            {/* Optional Settings Section */}
            <Section id="optional" title="Optional Settings" isExpanded={expandedSections.optional} onToggle={toggleSection}>
              <div>
                <label className={labelClass}>Narrative Tone</label>
                <select
                  value={blueprint.optional_metadata.narrative_tone}
                  onChange={(e) => {
                    setBlueprint(prev => ({
                      ...prev,
                      optional_metadata: { ...prev.optional_metadata, narrative_tone: e.target.value }
                    }));
                    trackManualUpdate('optional_metadata.narrative_tone', e.target.value);
                  }}
                  className={selectClass}
                >
                  <option value="Professional">Professional</option>
                  <option value="Casual">Casual</option>
                  <option value="Formal">Formal</option>
                  <option value="Friendly">Friendly</option>
                </select>
              </div>

              <div>
                <label className={labelClass}>Randomization Seed</label>
                <input
                  type="number"
                  value={blueprint.optional_metadata.randomization_seed || ''}
                  onChange={(e) => {
                    const value = e.target.value ? parseInt(e.target.value) : undefined;
                    setBlueprint(prev => ({
                      ...prev,
                      optional_metadata: { ...prev.optional_metadata, randomization_seed: value }
                    }));
                    trackManualUpdate('optional_metadata.randomization_seed', value);
                  }}
                  placeholder="Optional seed value"
                  className={inputClass}
                />
              </div>

              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-slate-700">Enable Logging</label>
                <button
                  onClick={() => setBlueprint(prev => ({
                    ...prev,
                    optional_metadata: { ...prev.optional_metadata, logging: !prev.optional_metadata.logging }
                  }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    blueprint.optional_metadata.logging ? 'bg-emerald-500' : 'bg-slate-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      blueprint.optional_metadata.logging ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-slate-700">Enable Real World Problems</label>
                <button
                  onClick={() => setBlueprint(prev => ({
                    ...prev,
                    optional_metadata: { 
                      ...prev.optional_metadata, 
                      enable_real_world_problems: !prev.optional_metadata.enable_real_world_problems 
                    }
                  }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    blueprint.optional_metadata.enable_real_world_problems ? 'bg-emerald-500' : 'bg-slate-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      blueprint.optional_metadata.enable_real_world_problems ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </Section>
          </div>
        </div>

        {/* Right Column - Chat Interface */}
        <div className="w-1/2 bg-white overflow-hidden flex flex-col">
          <StudioPlusChat
            onBlueprintUpdate={handleBlueprintUpdate}
            onSessionReady={(session) => setStudioPlusSession(session)}
            onComplete={(complete) => setIsComplete(complete)}
            currentBlueprint={blueprint}
            manualUpdates={pendingManualUpdates}
            onManualUpdatesSent={() => {
              setPendingManualUpdates({});
              // Clear in-flight fields after response is processed
              inFlightFieldsRef.current = new Set();
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default StudioPlusBuilder;
